/**
 * 🔐 O LOGIN PEDE O CÓDIGO A QUEM TEM O SEGUNDO PASSO LIGADO (mock 11 do pacote 2, aprovado em 14/10/2026).
 *
 * `PortaoDuasEtapas` fica em volta do painel (PrivateRoute). Ele pergunta ao Supabase o nível da sessão:
 *   · conta SEM fator (a imensa maioria, a M5 inteira inclusive) → `proximo = aal1` → passa direto;
 *   · conta COM fator e sessão `aal1` (acabou de entrar só com a senha) → pede o código ANTES de entrar;
 *   · sessão já `aal2` → passa.
 * Erro ou demora para ler o nível → passa (quem tranca escrita sensível é o SERVIDOR, não esta tela; uma
 * falha aqui não pode deixar o painel em branco).
 *
 * `AvisoDuasEtapas` é a faixa do aviso de carência ("ative até DD/MM") e do bloqueio depois dela. Só
 * consulta a API quando a empresa ativa é da Regula: em qualquer outra empresa não renderiza nada nem chama
 * nada.
 */
import { lazy, Suspense, useContext, useEffect, useState, type ReactNode } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { EmpresaAtivaCtx } from "../hooks/empresaAtivaContext";
import { ehRegula } from "../lib/tenant";
import { nivelDaSessao, precisaDoCodigo } from "../lib/segundoFator";

// As telas (código e faixa) vêm sob demanda: o pacote inicial tem teto de 700 KB (Build & bundle size, #2986).
const DesafioDoCodigo = lazy(() => import("./PortaoDuasEtapasTelas").then((m) => ({ default: m.DesafioDoCodigo })));
const AvisoDuasEtapasDaRegula = lazy(() => import("./PortaoDuasEtapasTelas").then((m) => ({ default: m.AvisoDuasEtapasDaRegula })));

/** Quanto o portão espera o nível da sessão antes de desistir e deixar passar. */
const ESPERA_DO_NIVEL_MS = 3_000;

export function PortaoDuasEtapas({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const token = session?.access_token ?? null;
  // `undefined` = ainda lendo · `false` = passa · `true` = pede o código
  const [pede, setPede] = useState<boolean | undefined>(token ? undefined : false);
  useEffect(() => {
    if (!token) { setPede(false); return; }
    let vivo = true;
    // 🔴 NUNCA volta para "lendo" quando o token troca: a renovação de hora em hora desmontaria a página
    // aberta (tests/renovar-token-nao-desmonta-a-pagina). A releitura roda por trás; só muda a tela se
    // a sessão passou a precisar do código. Na 1ª montagem o estado já nasce "lendo".
    const desiste = window.setTimeout(() => { if (vivo) setPede((p) => (p === undefined ? false : p)); }, ESPERA_DO_NIVEL_MS);
    nivelDaSessao()
      .then((n) => { if (vivo) setPede(precisaDoCodigo(n)); })
      .catch(() => { if (vivo) setPede((p) => (p === undefined ? false : p)); })
      .finally(() => window.clearTimeout(desiste));
    return () => { vivo = false; window.clearTimeout(desiste); };
  }, [token]);
  if (pede === undefined) return null;
  if (pede) {
    return <Suspense fallback={null}><DesafioDoCodigo onConfirmado={() => setPede(false)} /></Suspense>;
  }
  return <>{children}</>;
}

/**
 * A faixa: obrigada e ainda sem o segundo passo. Some na própria tela de ativar e fora da Regula.
 * Fora da Regula (ou numa árvore sem React Query, como testes do roteador) não monta consulta nenhuma.
 */
export function AvisoDuasEtapas() {
  const temCliente = useContext(QueryClientContext) !== undefined;
  const daRegula = ehRegula(useContext(EmpresaAtivaCtx)?.ativa ?? null);
  return temCliente && daRegula ? <Suspense fallback={null}><AvisoDuasEtapasDaRegula /></Suspense> : null;
}
